import React, { useState, useEffect } from 'react';
import { Player, Match, PlayerBadgesData } from '../types';
import { storage } from '../utils/storage';
import { calculatePlayerStats, getAllTimeStats, calculateDetailedStats } from '../utils/calculations';
import { toTurkishUpperCase } from '../utils/turkishUtils';
import { ensureHttps } from '../utils/urlUtils';
import TeamLogo from './TeamLogo';
import GoalIcon from './GoalIcon';
import AssistIcon from './AssistIcon';
import { PlayerBadges } from './PlayerBadges';
import './PlayerProfileModal.css';

interface PlayerProfileModalProps {
  player: Player;
  onClose: () => void;
  isCompact?: boolean;
  onCardClick?: (match: Match) => void;
}

const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({ player, onClose, isCompact = false, onCardClick }) => {
  const [displayPlayer, setDisplayPlayer] = useState<Player>(player);
  const [seasons, setSeasons] = useState<any[]>([]);
  const [currentSeasonId, setCurrentSeasonId] = useState<string>('');
  const [selectedSeason, setSelectedSeason] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [selectedPlayerForProfile, setSelectedPlayerForProfile] = useState<Player | null>(null);
  const [matches, setMatches] = useState<Match[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [detailedStats, setDetailedStats] = useState<any>(null);
  const isAdmin = storage.isAdminMode();

  useEffect(() => {
    setDisplayPlayer(player);
  }, [player]);

  useEffect(() => {
    const loadData = async () => {
      const loadedSeasons = await storage.getSeasons();
      const loadedCurrentSeasonId = await storage.getCurrentSeason();
      const loadedMatches = await storage.getMatches();
      const loadedPlayers = await storage.getPlayers();
      
      setSeasons(loadedSeasons);
      setCurrentSeasonId(loadedCurrentSeasonId || '');
      setMatches(loadedMatches);
      setPlayers(loadedPlayers);
      
      const initialSeason = loadedCurrentSeasonId || 'all';
      setSelectedSeason(initialSeason);
      
      const loadedStats = initialSeason === 'all' 
        ? await getAllTimeStats(player.id)
        : await calculatePlayerStats(player.id, initialSeason);
      setStats(loadedStats);
      setDetailedStats(calculateDetailedStats(loadedStats));
    };
    loadData();
  }, [player.id]);

  useEffect(() => {
    const updateStats = async () => {
      if (!selectedSeason) return;
      const loadedStats = selectedSeason === 'all' 
        ? await getAllTimeStats(player.id)
        : await calculatePlayerStats(player.id, selectedSeason);
      setStats(loadedStats);
      setDetailedStats(calculateDetailedStats(loadedStats));
    };
    updateStats();
  }, [selectedSeason, player.id]);

  const playerMatches = matches.filter(m => 
    m && m.blackTeam && m.whiteTeam && 
    (m.blackTeam.includes(player.id) || m.whiteTeam.includes(player.id))
  );

  const dateFilteredMatches = selectedDate 
    ? playerMatches.filter(m => m.date === selectedDate)
    : playerMatches;

  // Oyuncu kartlarını maçlardan al (sezon bazlı)
  const getPlayerCardsFromMatches = (): Array<{ image: string; match: Match }> => {
    const cards: Array<{ image: string; match: Match }> = [];
    
    matches.forEach(match => {
      // Sezon filtresi
      if (selectedSeason !== 'all' && match.seasonId !== selectedSeason) {
        return;
      }
      
      // Oyuncu bu maçta oynadı mı?
      const playedInMatch = match.blackTeam.includes(player.id) || match.whiteTeam.includes(player.id);
      if (!playedInMatch) return;
      
      // Siyah kart kontrolü
      if (match.blackCard === player.id && match.blackCardImage) {
        cards.push({ image: match.blackCardImage, match });
      }
      
      // MVDP kontrolü
      if (match.mvdp === player.id && match.mvdpImage) {
        cards.push({ image: match.mvdpImage, match });
      }
    });
    
    return cards;
  };

  const playerCardsForSeason = getPlayerCardsFromMatches();

  // Oyuncu kartını sezon bazlı al
  const getPlayerCardForSeason = (seasonId: string): string | undefined => {
    if (!player.playerCard) return undefined;
    
    // Eski format (string) ise, mevcut sezona atanır
    if (typeof player.playerCard === 'string') {
      // Eğer seçilen sezon mevcut sezon ise göster
      if (seasonId === currentSeasonId) {
        return player.playerCard;
      }
      return undefined;
    }
    
    // Yeni format (sezon bazlı) ise
    return player.playerCard[seasonId];
  };

  // Son sezondaki kartı bul
  const getLatestPlayerCard = (): string | undefined => {
    if (!player.playerCard) return undefined;
    
    // Eski format (string) ise
    if (typeof player.playerCard === 'string') {
      return player.playerCard;
    }
    
    // Yeni format (sezon bazlı) ise, en son sezondaki kartı bul
    const playerCardBySeason = player.playerCard as { [seasonId: string]: string };
    const seasonIds = Object.keys(playerCardBySeason);
    if (seasonIds.length === 0) return undefined;
    
    // Tüm sezonları sayısal sıraya göre sırala (en yüksek numara = en yeni sezon)
    const seasonsWithCards = seasons
      .filter(s => seasonIds.includes(s.id) && playerCardBySeason[s.id]) // Sadece kartı olan sezonları al
      .map(s => {
        // Sezon isimlerinden sayı çıkar (1. Sezon -> 1, 2. Sezon -> 2, vb.)
        const numMatch = s.name.match(/\d+/);
        const num = numMatch ? parseInt(numMatch[0]) : 0;
        return { season: s, num, id: s.id };
      })
      .sort((a, b) => b.num - a.num); // Büyükten küçüğe sırala (en yeni sezon en başta)
    
    if (seasonsWithCards.length === 0) return undefined;
    
    // En yüksek numaralı sezonu al (en yeni sezon)
    const latestSeason = seasonsWithCards[0].season;
    return playerCardBySeason[latestSeason.id];
  };

  const currentPlayerCard = selectedSeason === 'all' 
    ? getLatestPlayerCard() // Tüm zamanlar seçildiğinde son sezon kartını göster
    : getPlayerCardForSeason(selectedSeason);

  const getPlayerName = (playerId: string) => {
    return toTurkishUpperCase(players.find(p => p.id === playerId)?.name || 'Bilinmiyor');
  };

  const handlePlayerClick = (playerId: string) => {
    const clickedPlayer = players.find(p => p.id === playerId);
    if (clickedPlayer) {
      setSelectedPlayerForProfile(clickedPlayer);
    }
  };

  // Compact mod için de son sezon kartını göster
  const compactPlayerCard = getLatestPlayerCard();

  // Stats yüklenene kadar bekle
  if (!stats || !detailedStats) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            Yükleniyor...
          </div>
        </div>
      </div>
    );
  }

  if (isCompact) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content compact" onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={onClose}>×</button>
          <div className="player-header">
            <h2>{toTurkishUpperCase(player.name)}</h2>
            {compactPlayerCard && (
              <img src={ensureHttps(compactPlayerCard)} alt={`${player.name} Kartı`} className="player-card-image" />
            )}
            {((displayPlayer.badges && (displayPlayer.badges.country || displayPlayer.badges.league || displayPlayer.badges.team || (displayPlayer.badges.styles?.length || 0) > 0 || (displayPlayer.badges.chemistries?.length || 0) > 0)) || isAdmin) ? (
              <PlayerBadges
                player={displayPlayer}
                isAdmin={isAdmin}
                onSave={async (updatedBadges: PlayerBadgesData) => {
                  try {
                    const loadedPlayers = await storage.getPlayers({ forceRefresh: true });
                    const updatedPlayers = loadedPlayers.map(p =>
                      p.id === player.id ? { ...p, badges: updatedBadges } : p
                    );
                    await storage.savePlayers(updatedPlayers);
                    setDisplayPlayer(prev => ({ ...prev, badges: updatedBadges }));
                  } catch (err) {
                    console.error('[PlayerBadges] Kayıt hatası:', err);
                  }
                }}
              />
            ) : null}
            <div className="player-info">
              <span>Maç: {stats.matches}</span>
              {player.position !== 'KL' && <span>Gol Katkısı: {stats.goals + stats.assists}</span>}
              <span>G: {stats.wins} B: {stats.draws} M: {stats.losses}</span>
              {stats.blackCards > 0 && <span>Siyah Kart: {stats.blackCards}</span>}
              {stats.mvdpCards > 0 && <span>MVDP: {stats.mvdpCards}</span>}
              {player.position === 'KL' ? (
                <>
                  {stats.goals > 0 && <span>Gol: {stats.goals}</span>}
                  {stats.assists > 0 && <span>Asist: {stats.assists}</span>}
                </>
              ) : (
                <>
                  <span>Gol: {stats.goals}</span>
                  <span>Asist: {stats.assists}</span>
                </>
              )}
            </div>
          </div>
          <div className="player-stats-compact">
            <div>Puan: {stats.points}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
          <div className="player-header">
            <h2>{toTurkishUpperCase(player.name)}</h2>
            {isAdmin && selectedSeason !== 'all' && (
            <div className="admin-upload">
              <label>
                Oyuncu Kartı Yükle
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    if (!e.target.files || !e.target.files[0]) return;
                    const file = e.target.files[0];
                    
                    try {
                      // Görseli hosting'e yükle (base64 yerine URL kullan)
                      const imageUrl = await storage.uploadImage(file, `players/cards/${player.id}-${selectedSeason}-${Date.now()}-${file.name}`);
                      
                      console.log('[PlayerProfileModal] Oyuncu kartı yüklendi:', imageUrl);
                      
                      // Mevcut oyuncuları al (API'den - görselleri korumak için)
                      const loadedPlayers = await storage.getPlayers({ forceRefresh: true });
                      const currentPlayer = loadedPlayers.find(p => p.id === player.id);
                      
                      if (!currentPlayer) {
                        throw new Error('Oyuncu bulunamadı!');
                      }
                      
                      const updatedPlayers = loadedPlayers.map(p => {
                        if (p.id === player.id) {
                          // Mevcut playerCard'ı kontrol et
                          let playerCardBySeason: { [seasonId: string]: string };
                          
                          // ÖNEMLİ: Mevcut playerCard'ı koru
                          const existingPlayerCard = p.playerCard || currentPlayer.playerCard;
                          
                          if (!existingPlayerCard) {
                            // Hiç kart yoksa yeni oluştur
                            playerCardBySeason = { [selectedSeason]: imageUrl };
                          } else if (typeof existingPlayerCard === 'string') {
                            // Eski format (string) ise, mevcut sezona atanmış olarak kabul et ve yeni sezona ekle
                            playerCardBySeason = {
                              [currentSeasonId]: existingPlayerCard,
                              [selectedSeason]: imageUrl
                            };
                          } else {
                            // Yeni format (sezon bazlı) ise, mevcut kartları koru ve seçilen sezona ekle
                            playerCardBySeason = {
                              ...(existingPlayerCard as { [seasonId: string]: string }),
                              [selectedSeason]: imageUrl
                            };
                          }
                          
                          // Tüm diğer alanları koru (özellikle görselleri)
                          return { 
                            ...p, 
                            // ÖNEMLİ: Tüm görselleri koru
                            image: p.image || currentPlayer.image,
                            profileImage: p.profileImage || currentPlayer.profileImage,
                            playerCard: playerCardBySeason,
                            stats: p.stats || currentPlayer.stats || {},
                            playerCards: p.playerCards || currentPlayer.playerCards || (Array.isArray(p.playerCards) ? [] : {})
                          };
                        }
                        return p;
                      });
                      
                      // Veritabanına kaydet
                      await storage.savePlayers(updatedPlayers);
                      
                      console.log('[PlayerProfileModal] Oyuncu kartı kaydedildi, doğrulama yapılıyor...');
                      
                      // API'nin güncellenmesi için bekleme (artırıldı)
                      await new Promise(resolve => setTimeout(resolve, 2000));
                      
                      // Doğrulama - API'den oyuncuyu çek
                      const verifyPlayers = await storage.getPlayers();
                      const verifiedPlayer = verifyPlayers.find(p => p.id === player.id);
                      
                      if (verifiedPlayer && verifiedPlayer.playerCard) {
                        const cardValue = typeof verifiedPlayer.playerCard === 'string' 
                          ? verifiedPlayer.playerCard 
                          : verifiedPlayer.playerCard[selectedSeason];
                        
                        if (cardValue === imageUrl) {
                          console.log('[PlayerProfileModal] ✅ Oyuncu kartı başarıyla kaydedildi ve doğrulandı');
                          alert('Oyuncu kartı başarıyla yüklendi!');
                        } else {
                          console.warn('[PlayerProfileModal] ⚠️ Oyuncu kartı farklı URL ile kaydedilmiş:', {
                            expected: imageUrl,
                            actual: cardValue
                          });
                          alert('Oyuncu kartı yüklendi ancak doğrulanamadı. Sayfa yenilenecek.');
                        }
                      } else {
                        console.error('[PlayerProfileModal] ❌ Oyuncu kartı kaydedilemedi');
                        alert('UYARI: Oyuncu kartı yüklenemedi. Lütfen tekrar deneyin.');
                      }
                      
                      // Sayfayı yenile (kullanıcı yeni kartı görebilsin)
                      // ÖNEMLİ: Cache'i temizlemeden önce API'nin güncellenmesini bekle
                      await new Promise(resolve => setTimeout(resolve, 1000));
                      window.location.reload();
                    } catch (error: any) {
                      console.error('[PlayerProfileModal] Oyuncu kartı yükleme hatası:', error);
                      const errorMessage = error?.message || 'Bilinmeyen hata';
                      alert('Oyuncu kartı yüklenirken bir hata oluştu: ' + errorMessage + '\n\nLütfen Console\'u (F12) kontrol edin.');
                    }
                    
                    // Input'u temizle
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          )}
          {currentPlayerCard && (
            <img 
              src={ensureHttps(currentPlayerCard)} 
              alt={`${player.name} Kartı`} 
              className="player-card-image"
              onLoad={() => {
                console.log('[PlayerProfileModal] Oyuncu kartı başarıyla yüklendi:', currentPlayerCard);
              }}
              onError={(e) => {
                console.error('[PlayerProfileModal] Oyuncu kartı yüklenemedi:', {
                  url: currentPlayerCard,
                  player: player.name,
                  isBase64: currentPlayerCard.startsWith('data:image'),
                  urlLength: currentPlayerCard.length
                });
                const target = e.target as HTMLImageElement;
                const parent = target.parentElement;
                if (!parent) return;
                
                // Önce mevcut error div'i kontrol et
                const existingError = parent.querySelector('.player-card-error');
                if (existingError) return;
                
                target.style.display = 'none';
                
                // Base64 string ise (eski format) uyarı göster
                if (currentPlayerCard.startsWith('data:image')) {
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'player-card-error';
                  errorDiv.style.cssText = 'color: #ff4444; padding: 10px; text-align: center; background: #2a2a2a; border-radius: 8px; margin: 10px 0;';
                  errorDiv.textContent = 'Eski format görsel. Lütfen yönetici modundan yeni görsel yükleyin.';
                  parent.insertBefore(errorDiv, target);
                } else {
                  // URL formatında ama yüklenemiyor
                  const errorDiv = document.createElement('div');
                  errorDiv.className = 'player-card-error';
                  errorDiv.style.cssText = 'color: #ff4444; padding: 10px; text-align: center; background: #2a2a2a; border-radius: 8px; margin: 10px 0;';
                  errorDiv.innerHTML = `Görsel yüklenemedi.<br><small style="color: #888;">URL: ${currentPlayerCard.substring(0, 60)}...</small>`;
                  parent.insertBefore(errorDiv, target);
                }
              }}
            />
          )}
        </div>

        {((displayPlayer.badges && (displayPlayer.badges.country || displayPlayer.badges.league || displayPlayer.badges.team || (displayPlayer.badges.styles?.length || 0) > 0 || (displayPlayer.badges.chemistries?.length || 0) > 0)) || isAdmin) ? (
          <PlayerBadges
            player={displayPlayer}
            isAdmin={isAdmin}
            onSave={async (updatedBadges: PlayerBadgesData) => {
              try {
                const loadedPlayers = await storage.getPlayers({ forceRefresh: true });
                const updatedPlayers = loadedPlayers.map(p =>
                  p.id === player.id ? { ...p, badges: updatedBadges } : p
                );
                await storage.savePlayers(updatedPlayers);
                setDisplayPlayer(prev => ({ ...prev, badges: updatedBadges }));
              } catch (err) {
                console.error('[PlayerBadges] Kayıt hatası:', err);
              }
            }}
          />
        ) : null}

        <div className="season-selector">
          <label>
            Sezon:
            <select value={selectedSeason} onChange={(e) => setSelectedSeason(e.target.value)}>
              <option value="all">Tüm Zamanlar</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id}>{season.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="stats-section">
          <h3>Genel İstatistikler</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Maç</span>
              <span className="stat-value">{stats.matches}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Galibiyet</span>
              <span className="stat-value">{stats.wins}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Beraberlik</span>
              <span className="stat-value">{stats.draws}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Mağlubiyet</span>
              <span className="stat-value">{stats.losses}</span>
            </div>
            {player.position !== 'KL' && (
              <div className="stat-item">
                <span className="stat-label">Gol Katkısı</span>
                <span className="stat-value">{stats.goals + stats.assists}</span>
              </div>
            )}
            {player.position === 'KL' ? (
              <>
                {stats.goals > 0 && (
                  <div className="stat-item">
                    <span className="stat-label">Gol</span>
                    <span className="stat-value">{stats.goals}</span>
                  </div>
                )}
                {stats.assists > 0 && (
                  <div className="stat-item">
                    <span className="stat-label">Asist</span>
                    <span className="stat-value">{stats.assists}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="stat-item">
                  <span className="stat-label">Gol</span>
                  <span className="stat-value">{stats.goals}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Asist</span>
                  <span className="stat-value">{stats.assists}</span>
                </div>
              </>
            )}
            {stats.blackCards > 0 && (
              <div className="stat-item">
                <span className="stat-label">Siyah Kart</span>
                <span className="stat-value">{stats.blackCards}</span>
              </div>
            )}
            {stats.mvdpCards > 0 && (
              <div className="stat-item">
                <span className="stat-label">MVDP Kart</span>
                <span className="stat-value">{stats.mvdpCards}</span>
              </div>
            )}
          </div>
        </div>

        {playerCardsForSeason.length > 0 && (
          <div className="player-cards-section">
            <h3>Siyah/MVDP Kartları</h3>
            <div className="player-cards-grid">
              {playerCardsForSeason.map((cardData, index) => (
                <img 
                  key={index} 
                  src={ensureHttps(cardData.image)} 
                  alt={`Kart ${index + 1}`} 
                  className="player-card-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMatch(cardData.match);
                  }}
                  style={{ cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
        )}

        <div className="detailed-stats-section">
          <h3>Detaylı İstatistikler</h3>
          <div className="detailed-stats">
            <div className="stat-item">
              <span className="stat-label">Galibiyet Oranı</span>
              <span className="stat-value">{detailedStats.winRate}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Beraberlik Oranı</span>
              <span className="stat-value">{detailedStats.drawRate}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Mağlubiyet Oranı</span>
              <span className="stat-value">{detailedStats.lossRate}</span>
            </div>
            {player.position !== 'KL' && (
              <div className="stat-item">
                <span className="stat-label">MAÇ BAŞINA GOL KATKISI</span>
                <span className="stat-value">{detailedStats.goalContributionPercentage}</span>
              </div>
            )}
            <div className="stat-item">
              <span className="stat-label">Maç Başına Gol</span>
              <span className="stat-value">{detailedStats.goalsPerMatch}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Maç Başına Asist</span>
              <span className="stat-value">{detailedStats.assistsPerMatch}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Maç Başına Puan</span>
              <span className="stat-value">{detailedStats.pointsPerMatch}</span>
            </div>
          </div>
        </div>

        <div className="date-selector">
          <label>
            Tarih Seç:
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </label>
          {selectedDate && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedDate('');
              }}
              className="clear-date-button"
            >
              Temizle
            </button>
          )}
        </div>

        {selectedDate && dateFilteredMatches.length > 0 && (
          <div className="match-details">
            {dateFilteredMatches.map(match => {
              // Oyuncu bu maçta oynadı mı kontrol et
              const isInBlackTeam = match.blackTeam.includes(player.id);
              const isInWhiteTeam = match.whiteTeam.includes(player.id);
              const playedInMatch = isInBlackTeam || isInWhiteTeam;
              
              // Eğer oyuncu bu maçta oynamadıysa gösterme
              if (!playedInMatch) {
                return null;
              }
              
              // Sadece bu oyuncunun gol/asist bilgilerini göster
              const playerEvents = match.events.filter(e => 
                e.scorerId === player.id || e.assistId === player.id
              );
              
              return (
                <div key={match.id} className="match-detail-item">
                  <div className="match-info">
                    <div className="match-date-large">
                      {new Date(match.date).toLocaleDateString('tr-TR')}
                    </div>
                    <div className="match-score-large">
                      <TeamLogo team="black" size={56} />
                      <span className="team-name">SİYAH</span> {match.blackScore} - {match.whiteScore} <span className="team-name">BEYAZ</span>
                      <TeamLogo team="white" size={56} />
                    </div>
                  </div>

                  {playerEvents.length > 0 && (
                    <div className="match-events-detail">
                      <h3>Gol ve Asistler:</h3>
                      {playerEvents.map(event => (
                        <div key={event.id} className={`event-detail-item event-detail-item-${event.team}`}>
                          <span className="minute">{event.minute}'</span>
                          <span className="team-badge">
                            <TeamLogo team={event.team} size={28} />
                          </span>
                          <GoalIcon size={20} team={event.team} />
                          {event.guestScorerName ? (
                            <span>{toTurkishUpperCase(event.guestScorerName)}{event.ownGoal && ' (KK)'}</span>
                          ) : (
                            <span className={`player-name-${event.team}`}>
                              {event.scorerId ? getPlayerName(event.scorerId) : ''}{event.ownGoal && ' (KK)'}
                            </span>
                          )}
                          {(event.guestAssistName || event.assistId) && (
                            <>
                              <AssistIcon size={20} team={event.team} />
                              {event.guestAssistName ? (
                                <span>{toTurkishUpperCase(event.guestAssistName)}</span>
                              ) : (
                                <span className={`player-name-${event.team}`}>
                                  {event.assistId ? getPlayerName(event.assistId) : ''}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {(match.blackCard === player.id && match.blackCardImage) || (match.mvdp === player.id && match.mvdpImage) ? (
                    (match.blackCard === player.id && match.blackCardImage && match.mvdp === player.id && match.mvdpImage) ? (
                      <div className="match-awards-container">
                        <div className="match-award-single">
                          <div className="award-header">
                            <strong>Siyah Kart: </strong>
                            <span>{getPlayerName(match.blackCard)}</span>
                          </div>
                          <div className="award-image-container-square">
                            <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image-square" />
                          </div>
                        </div>
                        <div className="match-award-single">
                          <div className="award-header">
                            <strong>MVDP Kart: </strong>
                            <span>{getPlayerName(match.mvdp)}</span>
                          </div>
                          <div className="award-image-container-square">
                            <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image-square" />
                          </div>
                        </div>
                      </div>
                    ) : match.blackCard === player.id && match.blackCardImage ? (
                      <div className="match-award">
                        <div className="award-header">
                          <strong>Siyah Kart: </strong>
                          <span>{getPlayerName(match.blackCard)}</span>
                        </div>
                        <div className="award-image-container">
                          <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image" />
                        </div>
                      </div>
                    ) : match.mvdp === player.id && match.mvdpImage ? (
                      <div className="match-award">
                        <div className="award-header">
                          <strong>MVDP Kart: </strong>
                          <span>{getPlayerName(match.mvdp)}</span>
                        </div>
                        <div className="award-image-container">
                          <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image" />
                        </div>
                      </div>
                    ) : null
                  ) : null}

                  {match.matchImage && (
                    <div className="match-image-container">
                      <img src={ensureHttps(match.matchImage)} alt="Maç Kadrosu" className="match-image-vertical" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedMatch && (
        <MatchDetailModal
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onPlayerClick={handlePlayerClick}
          getPlayerName={getPlayerName}
        />
      )}

      {selectedPlayerForProfile && (
        <PlayerProfileModal
          player={selectedPlayerForProfile}
          onClose={() => setSelectedPlayerForProfile(null)}
          isCompact={false}
          onCardClick={(match) => {
            setSelectedPlayerForProfile(null);
            setSelectedMatch(match);
          }}
        />
      )}
    </div>
  );
};

// MatchDetailModal component for PlayerProfileModal
interface MatchDetailModalProps {
  match: Match;
  onClose: () => void;
  onPlayerClick: (playerId: string) => void;
  getPlayerName: (playerId: string) => string;
}

const MatchDetailModal: React.FC<MatchDetailModalProps> = ({ match, onClose, onPlayerClick, getPlayerName }) => {
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div className="match-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        <h2>Maç Detayları</h2>
        
        <div className="match-info">
          <div className="match-date-large">
            {new Date(match.date).toLocaleDateString('tr-TR')}
          </div>
          <div className="match-score-large">
            <TeamLogo team="black" size={56} />
            <span className="team-name">SİYAH</span> {match.blackScore} - {match.whiteScore} <span className="team-name">BEYAZ</span>
            <TeamLogo team="white" size={56} />
          </div>
        </div>

        <div className="match-events-detail">
          <h3>Gol ve Asistler:</h3>
          {[...match.events].sort((a, b) => a.minute - b.minute).map(event => (
            <div key={event.id} className={`event-detail-item event-detail-item-${event.team}`}>
              <span className="minute">{event.minute}'</span>
              <span className="team-badge">
                <TeamLogo team={event.team} size={28} />
              </span>
              <GoalIcon size={20} team={event.team} />
              {event.guestScorerName ? (
                <span>{toTurkishUpperCase(event.guestScorerName)}{event.ownGoal && ' (KK)'}</span>
              ) : (
                <span 
                  className={`player-name-clickable player-name-${event.team}`}
                  onClick={() => event.scorerId && onPlayerClick(event.scorerId)}
                >
                  {event.scorerId ? getPlayerName(event.scorerId) : ''}{event.ownGoal && ' (KK)'}
                </span>
              )}
              {(event.guestAssistName || event.assistId) && (
                <>
                  <AssistIcon size={20} team={event.team} />
                  {event.guestAssistName ? (
                    <span>{toTurkishUpperCase(event.guestAssistName)}</span>
                  ) : (
                    <span 
                      className={`player-name-clickable player-name-${event.team}`}
                      onClick={() => event.assistId && onPlayerClick(event.assistId!)}
                    >
                      {event.assistId ? getPlayerName(event.assistId) : ''}
                    </span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {(match.blackCard && match.blackCardImage) || (match.mvdp && match.mvdpImage) ? (
          (match.blackCard && match.blackCardImage && match.mvdp && match.mvdpImage) ? (
            <div className="match-awards-container">
              <div className="match-award-single">
                <div className="award-header">
                  <strong>Siyah Kart: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.blackCard!)}
                  >
                    {getPlayerName(match.blackCard)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image-square" />
                </div>
              </div>
              <div className="match-award-single">
                <div className="award-header">
                  <strong>MVDP: </strong>
                  <span 
                    className="player-name-clickable" 
                    onClick={() => onPlayerClick(match.mvdp!)}
                  >
                    {getPlayerName(match.mvdp)}
                  </span>
                </div>
                <div className="award-image-container-square">
                  <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image-square" />
                </div>
              </div>
            </div>
          ) : (
            <>
              {match.blackCard && match.blackCardImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>Siyah Kart: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.blackCard!)}
                    >
                      {getPlayerName(match.blackCard)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.blackCardImage)} alt="Siyah Kart" className="award-image" />
                  </div>
                </div>
              )}
              {match.mvdp && match.mvdpImage && (
                <div className="match-award">
                  <div className="award-header">
                    <strong>MVDP: </strong>
                    <span 
                      className="player-name-clickable" 
                      onClick={() => onPlayerClick(match.mvdp!)}
                    >
                      {getPlayerName(match.mvdp)}
                    </span>
                  </div>
                  <div className="award-image-container">
                    <img src={ensureHttps(match.mvdpImage)} alt="MVDP Kart" className="award-image" />
                  </div>
                </div>
              )}
            </>
          )
        ) : null}

        {match.matchImage && (
          <div className="match-image-container">
            <img src={ensureHttps(match.matchImage)} alt="Maç Kadroları" className="match-image-vertical" />
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerProfileModal;



